import { useCallback, useEffect, useRef, useState } from "react";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import { DOMSerializer } from "@tiptap/pm/model";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import GlobalDragHandle from "tiptap-extension-global-drag-handle";
import AutoJoiner from "tiptap-extension-auto-joiner";
import { Bold, Heading2, Heading3, ImagePlus, Italic, Link2, Link2Off, List, ListOrdered, Loader2, Pilcrow, Plus, Quote, Redo2, Sparkles, Strikethrough, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useUpload } from "@/components/admin/Fields";
import { BLOCKS, blockBoundaryAt, insertBlock } from "@/components/admin/posts/blocks";
import { Figure } from "@/components/admin/posts/extensions/Figure";
import { Callout } from "@/components/admin/posts/extensions/Callout";
import { Cta } from "@/components/admin/posts/extensions/Cta";
import { EventEmbed } from "@/components/admin/posts/extensions/EventEmbed";
import { Video } from "@/components/admin/posts/extensions/Video";
import { SlashCommand } from "@/components/admin/posts/extensions/SlashCommand";
import { streamAssist } from "@/components/admin/posts/AiWriter";
import { compressImage } from "@/lib/media";
import { cn } from "@/lib/utils";

const SELECTION_AI = [{ id: "improve", label: "Forbetre" }, { id: "shorten", label: "Kortare" }, { id: "expand", label: "Lengre" }, { id: "nynorsk", label: "Nynorsk" }];

const Tool = ({ on, active, label, children, disabled, testId, className }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} disabled={disabled} aria-label={label} title={label} aria-pressed={active}
    className={cn("inline-flex h-8 min-w-8 items-center justify-center rounded-md px-1.5 text-foreground/70 transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-40", active && "bg-foreground text-background hover:bg-foreground hover:text-background", className)} data-testid={testId}>
    {children}
  </button>
);
const Sep = () => <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />;

const selectionHtml = (editor) => {
  const { from, to } = editor.state.selection;
  const div = document.createElement("div");
  div.appendChild(DOMSerializer.fromSchema(editor.schema).serializeFragment(editor.state.doc.slice(from, to).content));
  return div.innerHTML;
};
const blockEndOfSelection = (ed) => { const $to = ed.state.selection.$to; return $to.depth ? $to.after(1) : $to.pos; };

/**
 * The block editor (TipTap/ProseMirror). What you see is the published article. Blocks have a drag handle on hover,
 * «/» opens the block menu, blocks can be dragged in from the palette, photos can be dropped or pasted straight in,
 * and a selection gets a floating bubble with formatting + AI. `onReady(editor)` hands the instance to the AI writer.
 */
export const RichEditor = ({ value = "", onChange, onReady, className, testId = "post-editor", context = "" }) => {
  const fileRef = useRef(null);
  const pendingPos = useRef(null);
  const editorRef = useRef(null);
  const { upload, uploading } = useUpload();
  const [aiBusy, setAiBusy] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const aiCtrl = useRef(null);

  const addImages = async (files, pos) => {
    const ed = editorRef.current;
    if (!ed || !files.length) return;
    let at = pos ?? blockEndOfSelection(ed);
    for (const file of files) {
      const url = await upload(await compressImage(file).catch(() => file));
      if (!url) continue;
      ed.chain().focus().insertFigureAt(at, { src: url, alt: "" }).run();
      at += 2;
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] }, codeBlock: false, dropcursor: { color: "hsl(var(--sol))", width: 3, class: "hk-dropcursor" } }),
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false, autolink: true, defaultProtocol: "https", HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Placeholder.configure({ includeChildren: true, placeholder: ({ node, editor: ed }) => (node.type.name !== "paragraph" ? "" : ed.state.doc.childCount === 1 && ed.state.doc.firstChild.type.name === "paragraph" ? "Skriv saka her, trykk / for å setje inn ein blokk – eller lat AI lage eit utkast …" : "Skriv, eller trykk / for blokker") }),
      Figure, Callout, Cta, EventEmbed, Video, SlashCommand,
      GlobalDragHandle.configure({ dragHandleWidth: 24, scrollTreshold: 120 }),
      AutoJoiner.configure({ elementsToJoin: ["bulletList", "orderedList"] }),
    ],
    content: value || "",
    editorProps: {
      attributes: { class: "article-body", "data-testid": `${testId}-content` },
      handleDrop: (view, event) => {
        const blockId = event.dataTransfer?.getData("application/x-hk-block");
        const files = [...(event.dataTransfer?.files || [])].filter((f) => f.type.startsWith("image/"));
        if (!blockId && !files.length) return false;
        event.preventDefault();
        const pos = blockBoundaryAt(view, event.clientX, event.clientY);
        if (blockId) insertBlock(editorRef.current, blockId, pos);
        else addImages(files, pos);
        return true;
      },
      handlePaste: (_view, event) => {
        const files = [...(event.clipboardData?.files || [])].filter((f) => f.type.startsWith("image/"));
        if (!files.length) return false;
        addImages(files, null);
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => onChange?.(ed.isEmpty ? "" : ed.getHTML()),
  });
  editorRef.current = editor;

  const pickImage = useCallback((pos) => { pendingPos.current = pos ?? null; fileRef.current?.click(); }, []);
  useEffect(() => { if (editor) { editor.storage.hk = { pickImage }; onReady?.(editor); } }, [editor, onReady, pickImage]);
  useEffect(() => () => onReady?.(null), [onReady]);
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const cur = editor.isEmpty ? "" : editor.getHTML();
    if ((value || "") !== cur) editor.commands.setContent(value || "", false);
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Lenkje (https://…)", prev);
    if (url === null) return;
    if (!url.trim()) { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  }, [editor]);

  const aiOnSelection = async (action) => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    if (from === to) return;
    const html = selectionHtml(editor);
    aiCtrl.current?.abort();
    aiCtrl.current = new AbortController();
    setAiBusy(action);
    try {
      const out = await streamAssist({ action, text: html, context }, null, aiCtrl.current.signal);
      if (out != null) {
        const clean = out.replace(/^```html\s*/i, "").replace(/```\s*$/, "").trim();
        editor.chain().focus().insertContentAt({ from, to }, clean).run();
        toast.success("Teksten er oppdatert – Angre (⌘Z) tek deg tilbake");
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "AI-hjelpa feila");
    } finally { setAiBusy(null); setAiOpen(false); }
  };

  if (!editor) return <div className={cn("min-h-[480px] rounded-xl bg-secondary/40", className)} />;
  const c = () => editor.chain().focus();
  return (
    <div className={cn("editor-body rounded-xl bg-card ring-1 ring-foreground/[0.06] focus-within:ring-foreground/20", className)} data-testid={testId}>
      <div className="sticky top-[52px] z-10 flex flex-wrap items-center gap-0.5 rounded-t-xl border-b border-border bg-card/95 px-2 py-1.5 backdrop-blur sm:top-[60px]" role="toolbar" aria-label="Formatering" data-testid={`${testId}-toolbar`}>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="mr-1 inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground pl-2.5 pr-3 text-[12.5px] font-medium text-background transition-colors hover:bg-sol hover:text-ink" data-testid={`${testId}-insert`}><Plus className="h-3.5 w-3.5" /> Set inn</button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[300px] p-1.5" data-testid={`${testId}-insert-menu`}>
            <p className="px-2.5 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Blokker</p>
            <ul className="grid grid-cols-1">
              {BLOCKS.map((b) => <li key={b.id}><button type="button" onClick={() => insertBlock(editor, b.id)} className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left transition-colors hover:bg-secondary" data-testid={`${testId}-insert-${b.id}`}><span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary"><b.icon className="h-3.5 w-3.5" /></span><span className="min-w-0"><span className="block text-[13px] font-medium">{b.label}</span><span className="block truncate text-[11.5px] text-muted-foreground">{b.hint}</span></span></button></li>)}
            </ul>
          </PopoverContent>
        </Popover>
        <Tool on={() => c().setParagraph().run()} active={editor.isActive("paragraph")} label="Brødtekst" testId={`${testId}-p`}><Pilcrow className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Mellomtittel" testId={`${testId}-h2`}><Heading2 className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Undertittel" testId={`${testId}-h3`}><Heading3 className="h-4 w-4" /></Tool>
        <Sep />
        <Tool on={() => c().toggleBold().run()} active={editor.isActive("bold")} label="Feit" testId={`${testId}-bold`}><Bold className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleItalic().run()} active={editor.isActive("italic")} label="Kursiv" testId={`${testId}-italic`}><Italic className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleStrike().run()} active={editor.isActive("strike")} label="Gjennomstreka"><Strikethrough className="h-4 w-4" /></Tool>
        <Tool on={setLink} active={editor.isActive("link")} label="Lenkje" testId={`${testId}-link`}>{editor.isActive("link") ? <Link2Off className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}</Tool>
        <Sep />
        <Tool on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Punktliste" testId={`${testId}-ul`}><List className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Nummerert liste"><ListOrdered className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Sitat" testId={`${testId}-quote`}><Quote className="h-4 w-4" /></Tool>
        <Tool on={() => pickImage(null)} label="Set inn bilete" disabled={uploading} testId={`${testId}-image`}>{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}</Tool>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addImages([...(e.target.files || [])], pendingPos.current); e.target.value = ""; }} data-testid={`${testId}-image-input`} />
        <span className="ml-auto flex items-center">
          <Tool on={() => c().undo().run()} disabled={!editor.can().undo()} label="Angre" testId={`${testId}-undo`}><Undo2 className="h-4 w-4" /></Tool>
          <Tool on={() => c().redo().run()} disabled={!editor.can().redo()} label="Gjer om"><Redo2 className="h-4 w-4" /></Tool>
        </span>
      </div>

      <BubbleMenu editor={editor} tippyOptions={{ duration: 120, placement: "top", maxWidth: "none" }} shouldShow={({ editor: ed, from, to }) => from !== to && !ed.state.selection.node}>
        <div className="flex items-center gap-0.5 rounded-lg bg-ink p-1 text-paper shadow-lift [&_button]:text-paper/80 [&_button:hover]:bg-paper/15 [&_button:hover]:text-paper [&_button[aria-pressed=true]]:bg-sol [&_button[aria-pressed=true]]:text-ink" data-testid={`${testId}-bubble`}>
          {aiOpen ? (
            <>
              <span className="inline-flex h-8 items-center gap-1 pl-2 pr-1 text-[12px] font-medium text-sol"><Sparkles className="h-3.5 w-3.5" /> AI</span>
              {SELECTION_AI.map((a) => <button key={a.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aiOnSelection(a.id)} disabled={Boolean(aiBusy)} className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-[12.5px] font-medium disabled:opacity-50" data-testid={`${testId}-ai-${a.id}`}>{aiBusy === a.id && <Loader2 className="h-3 w-3 animate-spin" />}{a.label}</button>)}
              <Tool on={() => { aiCtrl.current?.abort(); setAiOpen(false); }} label="Lukk"><X className="h-3.5 w-3.5" /></Tool>
            </>
          ) : (
            <>
              <Tool on={() => c().toggleBold().run()} active={editor.isActive("bold")} label="Feit"><Bold className="h-4 w-4" /></Tool>
              <Tool on={() => c().toggleItalic().run()} active={editor.isActive("italic")} label="Kursiv"><Italic className="h-4 w-4" /></Tool>
              <Tool on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Mellomtittel"><Heading2 className="h-4 w-4" /></Tool>
              <Tool on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Sitat"><Quote className="h-4 w-4" /></Tool>
              <Tool on={setLink} active={editor.isActive("link")} label="Lenkje"><Link2 className="h-4 w-4" /></Tool>
              <span className="mx-0.5 h-5 w-px bg-paper/20" aria-hidden="true" />
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setAiOpen(true)} className="inline-flex h-8 items-center gap-1.5 rounded-md pl-2 pr-2.5 text-[12.5px] font-medium !text-sol hover:!bg-sol hover:!text-ink" data-testid={`${testId}-ai-open`}><Sparkles className="h-3.5 w-3.5" /> AI</button>
            </>
          )}
        </div>
      </BubbleMenu>

      <div className="relative px-7 py-6 sm:px-11 sm:py-8" data-testid={`${testId}-canvas`}><EditorContent editor={editor} /></div>
    </div>
  );
};
